import core, { App, GQL, Viewport } from '@gin-betting/core';
import matchList from '@gin-betting/match-list';
import { OrderedSet } from 'immutable';
import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch, compose } from 'redux';
import { createSelector, createStructuredSelector } from 'reselect';

import type { ListNavTab } from 'types';
import { NoResults } from 'components';
import { isMobile, ExtendedRouts } from 'app-constants';
import favorite from 'modules/favorite';
import { AsyncOutright } from 'modules/outright/async';
import { getDefaultVariablesFavorite, getByKey } from 'utils';
import { TopMenu, MobileTopMenu } from 'views/top-menu';
import messages from '../../messages';
import ResultsMatchList from '../Results/ResultsMatchList';
import UpcomingMatchList from '../Upcoming/UpcomingMatchList';
import './favoriteList.scss';

const {
    selectors: { selectFavoriteIds },
} = favorite;

const {
    appServiceLocator,
    utils: { checkOnRoute },
    entities: {
        normalizeEntity,
        selectors: { selectMatches },
    },
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;

const {
    constants: { GET_FAVORITE_MATCHES_SUCCESS },
} = matchList;

interface OwnProps {
    homeTabs: ListNavTab[];
}

interface MappedProps {
    lessThanOrEqual: Viewport.Breakpoints;
    matchIds: ReturnType<typeof selectFavoriteIds>;
    groupedIdsByTournament: ReturnType<typeof selectGroupedIdsByTournament>;
}

interface MappedActions {
    dispatch: Dispatch<App.TypedAction>;
}

type Props = OwnProps &
    WrappedComponentProps &
    MappedProps &
    MappedActions &
    RouteComponentProps<any>;

interface State {
    currFavoriteIds: OrderedSet<string>;
    fetchStatus: App.FetchStatus;
}

const mappingMsgByPathname = {
    [ExtendedRouts.FAVORITE_UPCOMING]: messages.upcomingFavorite,
    [ExtendedRouts.FAVORITE_OUTRIGHTS]: messages.outrightsFavorite,
    [ExtendedRouts.FAVORITE_RESULTS]: messages.resultsFavorite,
};

class FavoriteList extends React.Component<Props, State> {
    state: State = {
        currFavoriteIds: OrderedSet(),
        fetchStatus: App.FetchStatus.INITIAL,
    };

    componentDidMount() {
        this.requestFavoriteMatches({ withLoading: true });
    }

    componentDidUpdate(prevProps: Props) {
        const {
            matchIds,
            location: { pathname },
        } = this.props;

        const isLocationChanged = prevProps.location.pathname !== pathname;

        if (
            isLocationChanged ||
            // TODO: OPTIMIZE add more sophisticated check (maybe when `currFavoriteIds` changed)
            matchIds.subtract(prevProps.matchIds).count() > 0
        ) {
            this.requestFavoriteMatches({ withLoading: isLocationChanged });
        }

        if (isLocationChanged) {
            this.setState(prevState => ({
                currFavoriteIds: prevState.currFavoriteIds.clear(),
            }));
        }
    }

    requestFavoriteMatches = ({ withLoading }: { withLoading: boolean }) => {
        const { matchIds } = this.props;

        if (matchIds.count() === 0) {
            this.setState({
                fetchStatus: App.FetchStatus.LOADED,
            });

            return;
        }

        const isOutrights = checkOnRoute(ExtendedRouts.FAVORITE_OUTRIGHTS);
        const isUpcoming = checkOnRoute(ExtendedRouts.FAVORITE_UPCOMING);

        const variables: GQL.GetMatchesByEventIdsQueryVariables = {
            ...getDefaultVariablesFavorite(),
            sportEventIds: matchIds.toArray(),
            isTopMarkets: !isOutrights,
            withMarketsCount: isUpcoming,
            marketLimit: isOutrights ? null : 3,
        };

        if (withLoading) {
            this.setState({
                fetchStatus: App.FetchStatus.LOADING,
            });
        }

        appServiceLocator
            .get('apiClient')
            .makeRequest({
                method: 'getMatchesByEventIds',
                variables,
            })
            .then(response => {
                const incomeMatchIds = response.data.matches.map(
                    match => match.id
                );

                const normalizedData = normalizeEntity(response.data);

                this.props.dispatch({
                    type: GET_FAVORITE_MATCHES_SUCCESS,
                    payload: {
                        ...normalizedData,
                        listType: '/',
                        isTopMarkets: true,
                    },
                });

                this.setState((prevState: State) => ({
                    currFavoriteIds: matchIds.intersect(incomeMatchIds),
                    fetchStatus: withLoading
                        ? App.FetchStatus.LOADED
                        : prevState.fetchStatus,
                }));
            })
            .catch(err => {
                console.error(err);

                if (withLoading) {
                    this.setState({
                        fetchStatus: App.FetchStatus.LOADED,
                    });
                }
            });
    };

    render() {
        const { currFavoriteIds, fetchStatus } = this.state;
        const {
            matchIds,
            lessThanOrEqual,
            intl: { formatMessage },
            location: { pathname },
            groupedIdsByTournament,
            homeTabs,
        } = this.props;

        const filteredMatchIds = currFavoriteIds.intersect(matchIds);
        const isOutrights = checkOnRoute(ExtendedRouts.FAVORITE_OUTRIGHTS);

        const msgDesc = getByKey(mappingMsgByPathname, pathname);
        const msg = msgDesc ? formatMessage(msgDesc) : undefined;

        return (
            <div
                styleName="container"
                className="__app-FavoriteUpcoming-container"
            >
                {isMobile && <MobileTopMenu fetchStatus={fetchStatus} />}
                <TopMenu
                    homeTabs={homeTabs}
                    fetchStatus={
                        isMobile ? App.FetchStatus.INITIAL : fetchStatus
                    }
                />

                <div>
                    {/* TODO: refactor ternary-hell */}
                    {fetchStatus === App.FetchStatus.LOADED &&
                    !filteredMatchIds.count() ? (
                        <NoResults
                            key="-2"
                            type={App.PageType.LIST}
                            defaultMessage={msg}
                        />
                    ) : isOutrights ? (
                        groupedIdsByTournament.valueSeq().map(group => {
                            const filteredGroup = currFavoriteIds.intersect(
                                group as typeof matchIds
                            );

                            return filteredGroup.map(outrightId => (
                                <AsyncOutright
                                    key={outrightId}
                                    matchId={outrightId}
                                />
                            ));
                        })
                    ) : checkOnRoute(ExtendedRouts.FAVORITE_RESULTS) ? (
                        <ResultsMatchList
                            isSmall={lessThanOrEqual.small}
                            matchIds={filteredMatchIds}
                        />
                    ) : (
                        <UpcomingMatchList
                            isMedium={lessThanOrEqual.medium}
                            isSmall={lessThanOrEqual.small}
                            matchIds={filteredMatchIds}
                        />
                    )}
                </div>
            </div>
        );
    }
}

export const selectGroupedIdsByTournament = createSelector(
    selectFavoriteIds,
    selectMatches,
    (matchIds, matches) => {
        return matchIds.groupBy(matchId => {
            const match = matches.get(matchId);

            return !match || match.tournament;
        });
    }
);

const mapStateToProps = createStructuredSelector<MappedProps>({
    lessThanOrEqual: makeViewportSelector('lessThanOrEqual'),
    matchIds: selectFavoriteIds,
    groupedIdsByTournament: selectGroupedIdsByTournament,
});

const mapDispatchToProps = (dispatch: Dispatch<App.TypedAction>) => {
    return {
        dispatch,
    };
};

export default compose<React.ComponentClass<OwnProps>>(
    withRouter,
    connect<MappedProps, MappedActions, OwnProps>(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(FavoriteList);
